import React, { memo } from "react";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker as StyledTimePicker } from "@mui/x-date-pickers/TimePicker";
import { getIn } from "formik";
import cx from "classnames";

import { InfoTitle } from "../../InfoTitle";
import { ErrorMessage, ContexualHelp } from "../../Notifications";
import { areEqualFieldProps } from "../utils";
import { ReactComponent as CalenderIcon } from "../../../assets/icons/calendar.svg";

import { StyledTimePickerWrapper, StyledFormControl } from "./styled";

const TimePickerBase = ({
  field,
  label,
  disabled,
  infoTitle,
  placeholder = "__:__",
  timeFormat = "hh:mm a",
  InputProps = { inputProps: {} },
  form: { errors, touched, setFieldValue },
  timePickerProps = {},
  contextualHelpText,
  disableValue = null,
  openTo = "hours",
  contextualHelpProps = {},
  onChange = value => {
    setFieldValue(field.name, value);
  }
}) => {
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);
  const timeValue = field.value ? new Date(field.value || disableValue) : null;

  return (
    <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
      <StyledFormControl
        className={cx("formControl", "timePickerWithEndAdorment", { "Mui-error": isError })}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StyledTimePickerWrapper>
            <StyledTimePicker
              autoOk
              ampm={true}
              autoComplete="off"
              label={label}
              disabled={disabled}
              openTo={openTo}
              margin="normal"
              inputFormat={timeFormat}
              inputVariant="filled"
              variant="inline"
              placeholder={placeholder}
              error={false}
              {...field}
              {...timePickerProps}
              orientation="portrait"
              value={timeValue}
              onChange={onChange}
              showToolbar={false}
              ampmInClock={true}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="filled"
                  classes={{
                    root: "filledInput"
                  }}
                />
              )}
              className={"digitalClockSectionItemStyle"}
              InputProps={{
                disableUnderline: true
              }}
              inputProps={InputProps.inputProps}
              InputAdornmentProps={{
                position: "end",
                disablePointerEvents: disabled
              }}
              components={{
                OpenPickerIcon: CalenderIcon
              }}
              slotProps={{
                desktopTrapFocus: { isEnabled: () => true },
                keyboardButton: {
                  "aria-label": "change time"
                }
              }}
            />
          </StyledTimePickerWrapper>
        </LocalizationProvider>
        {infoTitle && <InfoTitle title={infoTitle} />}

        {isError && <ErrorMessage error={errorMessage} />}
      </StyledFormControl>
    </ContexualHelp>
  );
};

TimePickerBase.propTypes = {
  field: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  infoTitle: PropTypes.string,
  placeholder: PropTypes.string,
  timeFormat: PropTypes.string,
  openTo: PropTypes.string,
  InputProps: PropTypes.object,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  onChange: PropTypes.func
};

export const TimePicker = memo(TimePickerBase, areEqualFieldProps);
